'use client';

import type { HTMLAttributes } from 'react';
import React from 'react';
import Highlighter from 'react-highlight-words';
import clsx from 'clsx';
import {
  normalizeClipboardData,
  truncateText,
} from 'components/TextWithTruncation/utils';
import { css } from 'styled-components';
import variantStyles from 'styles/text-variants.module.css';
import { getTextColor } from 'theme-utils';
import invariant from 'tiny-invariant';
import { theme } from '@frameio/vapor-theme';
import styles from './Text.module.css';
import { TextWithTooltip } from './TextWithTooltip';
import type {
  TextAlign,
  TextDisplay,
  TextSupportedColorTokens,
  TextTruncation,
  TextVariantNumeric,
  TextVariants,
  TextWeights,
} from './types';
import { isMiddleTruncation } from './utils';

export type TextProps = HTMLAttributes<HTMLSpanElement> & {
  /** Alter the element with which to render the Text. Defaults to `span` */
  as?: any;
  /** Text to highlight. Only works when children of `Text` is also a string. */
  highlight?: string[] | string;
  /**
   * Enable a tooltip to display when the user hovers over the text and it
   * is overflowing.
   */
  enableTooltip?: boolean;
  /**
   * The typography variant to use, which alters the font-size and line-height.
   */
  variant?: TextVariants;
  /**
   * The type of truncation logic to use. If undefined, no truncation
   * will be applied.
   */
  truncate?: TextTruncation;
  /**
   * The color of the text to use. You must specify `inherit` if you wish to
   * inherit the color of the parent element.
   */
  color?: TextSupportedColorTokens | 'inherit';
  /**
   * Alternatively use inline, or inline-block to display the text.
   */
  display?: TextDisplay;
  /** Specify the text alignment. */
  textAlign?: TextAlign;
  /** Specify the font-weight */
  fontWeight?: TextWeights;
  /**
   * Please ensure the correct font assets are configured before using this option
   */
  italic?: boolean;
  /**
   * Specify if the numers should be rendered using tabular nums. You should
   * use this option any time numbers change frequently, such as with a progress
   * indicator.
   */
  variantNumeric?: TextVariantNumeric;
};

/**
 * The basic primitive component for rendering Text using vapor predefined
 * styles. [View component documentation](https://vapor.frame.io/vapor/components/text).
 *
 * Basic usage:
 *
 * ```jsx
 * <Text variant='body' color='primary'>Primary text</Text>
 * ```
 *
 * Enabling tooltips and truncation:
 *
 * ```jsx
 * <Text enableTooltip truncate>Text that will be truncated and have a tooltip</Text>
 * ```
 *
 * Text styles can also be used as a styled-components mixin:
 *
 * ```jsx
 * import { textStyles } from '@frameio/vapor';
 *
 * const MyText = styled.div`
 *   ${textStyles.body};
 *   color: ${theme.colors.text.primary};
 * `
 * ```
 */

export function getTruncateAttr(
  truncate: TextTruncation | undefined
): 'middle' | 'single' | 'multi' | undefined {
  if (isMiddleTruncation(truncate)) return 'middle';
  if (truncate === true) return 'single';
  if (typeof truncate === 'number') return 'multi';
  return undefined;
}

export const Text = React.forwardRef(
  (
    {
      display = 'block',
      truncate,
      color = 'body',
      fontWeight,
      variant = 'primary',
      enableTooltip = false,
      textAlign,
      highlight,
      ...other
    }: TextProps,
    ref: React.Ref<any>
  ) => {
    const textNode = (
      <TextNode
        ref={ref}
        color={color}
        display={display}
        fontWeight={fontWeight}
        highlight={highlight}
        textAlign={textAlign}
        truncate={truncate}
        variant={variant}
        {...other}
      />
    );

    if (enableTooltip && truncate) {
      return <TextWithTooltip truncate={truncate}>{textNode}</TextWithTooltip>;
    }

    return textNode;
  }
);

/**
 * Text node responsive for rendering either middle truncated text,
 * regular text, or a highlighted text.
 */

const TextNode = React.forwardRef(
  (
    {
      as: Tag = 'span',
      children,
      color = 'body',
      display = 'block',
      highlight,
      textAlign,
      variant = 'primary',
      fontWeight,
      truncate,
      variantNumeric,
      italic,
      style,
      className,
      ...other
    }: TextProps,
    ref: React.Ref<any>
  ) => {
    const cssVars = {
      // `inherit` on a custom property copies `--vapor-text-color` from the parent, not `color: inherit`. Instead, use `initial` to fallback to `color: inherit`.
      '--vapor-text-color':
        color === 'inherit' ? 'initial' : getTextColor(color),
      ...(fontWeight
        ? {
            '--vapor-text-font-weight': theme.fontWeights[fontWeight],
          }
        : undefined),
      ...(typeof truncate === 'number'
        ? { '--vapor-text-line-clamp': String(truncate) }
        : undefined),
      ...style,
    } as React.CSSProperties;

    const truncateAttr = getTruncateAttr(truncate);

    const combinedClassName = clsx(
      variantStyles[variant],
      styles.text,
      className
    );

    const sharedProps = {
      style: cssVars,
      className: combinedClassName,
      'data-display': display !== 'block' ? display : undefined,
      'data-truncate': truncateAttr,
      'data-text-align': textAlign,
      'data-italic': italic || undefined,
      'data-variant-numeric': variantNumeric,
      'data-font-weight': fontWeight || undefined,
      ...other,
    };

    if (isMiddleTruncation(truncate)) {
      invariant(
        typeof children === 'string',
        'Middle truncation requires that the child element is a string'
      );

      invariant(!highlight, 'Middle truncation does not support highlighting');

      const { initial, remainder } = truncateText({
        hasExtension: truncate.hasExtension,
        padding: truncate.padding,
        text: children,
      });

      return (
        <Tag {...sharedProps} onCopy={normalizeClipboardData}>
          <span ref={ref} className={styles.truncatedSpan}>
            {initial}
          </span>
          <span className={styles.truncatedSpanRemaining}>{remainder}</span>
        </Tag>
      );
    }

    let text = children;

    if (highlight && typeof text !== 'undefined') {
      invariant(
        typeof text === 'string',
        'children of Text must be a string to support highlighting'
      );

      text = (
        <Highlighter
          autoEscape={true}
          highlightClassName={styles.highlight}
          searchWords={Array.isArray(highlight) ? highlight : [highlight]}
          textToHighlight={text}
        />
      );
    }

    return (
      <Tag ref={ref} {...sharedProps}>
        {text}
      </Tag>
    );
  }
);

// @todo (VAPR-4032): Remove once consumers using truncateStyles are migrated to CSS modules
export const truncateStyles = css`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
